/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlusCircle, CornerDownRight, HelpCircle } from 'lucide-react';
import { serviceRequestTypeService } from '../../services/serviceRequestTypeService';
import toastService from '../../utils/toastService';

const HierarchyNodeAdder = ({
  typeUid,
  levels = [],
  nodes = [],
  onNodeCreated,
  externalTargetDepth = null,
  externalParentUid = null,
}) => {
  const [targetDepth, setTargetDepth] = useState(0);
  const [parentSelections, setParentSelections] = useState({}); // { 0: uid, 1: uid, ... }
  const [optionInput, setOptionInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync external pre-selection (e.g. when user clicks "+ Add Child" on a node in the tree view)
  useEffect(() => {
    if (externalTargetDepth !== null && externalTargetDepth >= 0 && externalTargetDepth < levels.length) {
      setTargetDepth(externalTargetDepth);
    }
  }, [externalTargetDepth, levels.length]);

  useEffect(() => {
    if (externalParentUid && targetDepth > 0) {
      // Find full ancestor path for the selected parent node
      const nodesById = new Map(nodes.map((n) => [n.id, n]));
      const nodesByUid = new Map(nodes.map((n) => [n.uid, n]));
      const parentNode = nodesByUid.get(externalParentUid) || nodesById.get(externalParentUid);

      if (parentNode) {
        const path = {};
        let curr = parentNode;
        while (curr) {
          path[curr.depth] = curr.uid;
          if (curr.parentId) {
            curr = nodesById.get(curr.parentId) || nodesByUid.get(curr.parentId) || null;
          } else {
            curr = null;
          }
        }
        setParentSelections(path);
      }
    }
  }, [externalParentUid, targetDepth, nodes]);

  // Handle changing target level
  const handleSelectTargetDepth = (depth) => {
    setTargetDepth(depth);
    setOptionInput('');
  };

  // Get active nodes at depth
  const getOptionsAtDepth = (depth, parentId) => {
    return nodes.filter((n) => {
      if (n.depth !== depth) return false;
      if (!n.isActive) return false;
      if (depth === 0) return true;
      return n.parentId === parentId || (nodes.find((p) => p.uid === parentId)?.id === n.parentId);
    });
  };

  // Handle parent dropdown change
  const handleParentChange = (depth, selectedUid) => {
    setParentSelections((prev) => {
      const next = { ...prev, [depth]: selectedUid };
      // Clear downstream selections if any
      for (let d = depth + 1; d < levels.length; d++) {
        delete next[d];
      }
      return next;
    });
  };

  // Immediate parent UID for target level
  const immediateParentUid = targetDepth === 0 ? null : parentSelections[targetDepth - 1] || null;

  // Validation
  const canSubmit = useMemo(() => {
    if (!optionInput.trim()) return false;
    if (targetDepth > 0 && !immediateParentUid) return false;
    return true;
  }, [optionInput, targetDepth, immediateParentUid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      const payload = {
        depth: targetDepth,
        parentUid: immediateParentUid || undefined,
        name: optionInput.trim(),
      };

      const res = await serviceRequestTypeService.createTreeNode(typeUid, payload);
      toastService.success(res.data?.message || 'Option(s) added successfully');
      setOptionInput('');
      if (onNodeCreated) {
        onNodeCreated();
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to add option'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!levels || levels.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-6 shadow-sm border border-base-content/10 text-center space-y-2">
        <p className="text-sm font-bold text-base-content/70">
          No hierarchy levels defined yet.
        </p>
        <p className="text-xs text-base-content/50">
          Please configure and save the hierarchy structure above before adding options.
        </p>
      </div>
    );
  }

  const currentLevelName = levels[targetDepth]?.name || `Level ${targetDepth + 1}`;

  return (
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-5">
      <div className="flex items-center gap-2.5 pb-3 border-b border-base-content/10">
        <div className="p-2 rounded-xl bg-primary/10 text-primary">
          <PlusCircle size={20} />
        </div>
        <div>
          <h2 className="text-base font-black text-base-content uppercase tracking-wider">
            Add Options to Hierarchy
          </h2>
          <p className="text-xs text-base-content/50">
            Select a target level, choose its parent dependencies, and add new option values.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Step 1: Target Level Selector Pills */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-black uppercase tracking-widest text-base-content/50 block">
            Step 1: Select Target Level to Add Options Under
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {levels.map((lvl, index) => {
              const isSelected = targetDepth === index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectTargetDepth(index)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-left transition-all ${
                    isSelected
                      ? 'bg-[#0099D9] text-white border-[#0099D9] shadow-sm font-bold ring-2 ring-[#0099D9]/30'
                      : 'bg-base-100 hover:bg-base-200 border-base-300 text-base-content font-medium'
                  }`}
                  style={
                    isSelected
                      ? { backgroundColor: '#0099D9', borderColor: '#0099D9', color: '#ffffff' }
                      : undefined
                  }
                >
                  <span
                    className={`level-number-badge w-6 h-6 rounded-lg text-xs font-black flex items-center justify-center shrink-0 transition-all ${
                      isSelected
                        ? 'selected shadow-sm bg-white text-[#0099D9]'
                        : 'bg-base-200 text-base-content/80 border border-base-300'
                    }`}
                    style={
                      isSelected
                        ? { backgroundColor: '#ffffff', color: '#0099D9', fontWeight: 900 }
                        : undefined
                    }
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className="text-xs truncate block font-bold"
                        style={isSelected ? { color: '#ffffff' } : undefined}
                      >
                        {lvl.name}
                      </span>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider ${
                          isSelected ? 'bg-white/25 text-white' : 'bg-base-200 text-base-content/60'
                        }`}
                        style={
                          isSelected
                            ? { backgroundColor: 'rgba(255, 255, 255, 0.25)', color: '#ffffff' }
                            : undefined
                        }
                      >
                        Level {index + 1}
                      </span>
                    </div>
                    <span
                      className="text-[10px] font-mono block"
                      style={
                        isSelected
                          ? { color: 'rgba(255, 255, 255, 0.85)' }
                          : { opacity: 0.6 }
                      }
                    >
                      {lvl.key}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Cascading Parent Pickers */}
        <div className="space-y-2.5 pt-1">
          <label className="text-[10px] font-black uppercase tracking-widest text-base-content/50 block">
            Step 2: Choose Parent Hierarchy ({targetDepth === 0 ? 'Root Level' : `Parents for Level ${targetDepth + 1}: ${currentLevelName}`})
          </label>

          {targetDepth === 0 ? (
            <div className="p-3 rounded-xl bg-base-200/50 border border-base-content/5 flex items-center gap-2 text-xs text-base-content/60 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 font-black text-[10px] uppercase tracking-wider shrink-0">
                Level 1
              </span>
              <span>
                <strong>{currentLevelName}</strong> is the top-level of this hierarchy (no parent selection needed).
              </span>
            </div>
          ) : (
            <div className="space-y-3 p-3.5 rounded-xl bg-base-200/40 border border-base-content/10">
              {Array.from({ length: targetDepth }).map((_, depthIdx) => {
                const parentLevel = levels[depthIdx];
                // Determine ancestor parent ID for this dropdown
                const ancestorId = depthIdx === 0 ? null : parentSelections[depthIdx - 1];
                const availableOptions = getOptionsAtDepth(depthIdx, ancestorId);
                const selectedVal = parentSelections[depthIdx] || '';
                const isPreviousSelected = depthIdx === 0 || !!parentSelections[depthIdx - 1];

                return (
                  <div key={depthIdx} className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-base-content">
                      <CornerDownRight size={14} className="text-primary" />
                      <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] font-black flex items-center justify-center">
                        {depthIdx + 1}
                      </span>
                      <span>Choose {parentLevel?.name} *</span>
                    </div>

                    <select
                      disabled={!isPreviousSelected}
                      value={selectedVal}
                      onChange={(e) => handleParentChange(depthIdx, e.target.value)}
                      className="w-full px-3 py-2 bg-base-100 border border-base-300 rounded-xl text-xs text-base-content focus:border-primary focus:outline-none disabled:opacity-50"
                    >
                      <option value="">
                        {!isPreviousSelected
                          ? `Please choose ${levels[depthIdx - 1]?.name} first`
                          : `-- Select ${parentLevel?.name} --`}
                      </option>
                      {availableOptions.map((opt) => (
                        <option key={opt.uid} value={opt.uid}>
                          {opt.name}
                        </option>
                      ))}
                    </select>

                    {isPreviousSelected && availableOptions.length === 0 && (
                      <p className="text-[10px] text-error font-medium">
                        No active {parentLevel?.name} options available under this parent. Please add {parentLevel?.name} options first.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Step 3: Option Name Input */}
        <div className="space-y-1.5 pt-1">
          <label htmlFor="optionInput" className="text-[10px] font-black uppercase tracking-widest text-base-content/50 block">
            Step 3: Option Name(s) under Level {targetDepth + 1} ({currentLevelName}) *
          </label>
          <div className="relative">
            <input
              id="optionInput"
              type="text"
              className="w-full px-4 py-2.5 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder:text-base-content/30"
              placeholder={
                targetDepth === 0
                  ? 'e.g. Elevators, Dishwashers'
                  : targetDepth === 1
                  ? 'e.g. Dishwash motor, Conveyor, Dishwasher motor for sink'
                  : 'e.g. Hobart, Bosch'
              }
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-base-content/50 font-medium">
            <HelpCircle size={12} className="text-primary shrink-0" />
            <span>
              Tip: You can add multiple options at once by separating them with commas (e.g. <code>Conveyor, Motor, Pump</code>).
            </span>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full sm:w-auto btn-premium px-6 py-2.5 text-xs font-bold justify-center"
          >
            {submitting ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Adding Option(s)...</span>
              </>
            ) : (
              <>
                <PlusCircle size={15} />
                <span>Add Level {targetDepth + 1} ({currentLevelName}) Option(s)</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

HierarchyNodeAdder.propTypes = {
  typeUid: PropTypes.string.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      key: PropTypes.string,
      depth: PropTypes.number,
    }),
  ),
  nodes: PropTypes.arrayOf(PropTypes.object),
  onNodeCreated: PropTypes.func,
  externalTargetDepth: PropTypes.number,
  externalParentUid: PropTypes.string,
};

export default HierarchyNodeAdder;
